import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { vault, apiErrorMessage, type VaultNote, type VaultKind } from '../lib/api';
import { notesToMarkdown, notesToJson, downloadText } from '../lib/exportNotes';
import { useApi } from '../hooks/useApi';
import { useTranslation, type TKey } from '../i18n';
import { EmptyState } from '../components/ui';

const KINDS: VaultKind[] = ['memo', 'quote', 'insight', 'feedback'];
const KIND_KEY: Record<VaultKind, TKey> = {
  memo: 'vault.kindMemo', quote: 'vault.kindQuote',
  insight: 'vault.kindInsight', feedback: 'vault.kindFeedback',
};

export default function VaultPage() {
  const { t } = useTranslation();
  const [refreshKey, setRefreshKey] = useState(0);
  const [kind, setKind] = useState<VaultKind>('memo');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<'all' | VaultKind>('all');

  const state = useApi(() => vault.list(), [refreshKey]);
  const all = state.status === 'success' ? state.data : [];

  const notes = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return all.filter((n) =>
      (filter === 'all' || n.kind === filter) &&
      (!ql || n.content.toLowerCase().includes(ql) || n.tags.some((tg) => tg.toLowerCase().includes(ql))),
    );
  }, [all, q, filter]);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!content.trim()) { toast.error(t('vault.needContent')); return; }
    setBusy(true);
    try {
      await vault.create({
        kind, content: content.trim(),
        tags: tags.split(',').map((s) => s.trim()).filter(Boolean),
      });
      toast.success(t('vault.added'));
      setContent(''); setTags('');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(apiErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  }

  async function remove(n: VaultNote) {
    try {
      await vault.remove(n.id);
      toast.success(t('vault.deleted'));
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(apiErrorMessage(err, t));
    }
  }

  return (
    <main className="container vault-page">
      <header className="vault-head">
        <div className="kicker">{t('vault.subtitle')}</div>
        <h1 className="vault-title">{t('vault.title')}</h1>
      </header>

      <form onSubmit={add} className="vault-form">
        <div className="vault-form__kinds">
          {KINDS.map((k) => (
            <button key={k} type="button"
              className={`vault-kind-tab${kind === k ? ' is-active' : ''}`}
              onClick={() => setKind(k)}>{t(KIND_KEY[k])}</button>
          ))}
        </div>
        <textarea className="prop-textarea" rows={3} value={content}
          onChange={(e) => setContent(e.target.value)} placeholder={t('vault.contentPlaceholder')} />
        <div className="vault-form__row">
          <input className="prop-choice__text" value={tags}
            onChange={(e) => setTags(e.target.value)} placeholder={t('vault.tagsPlaceholder')} />
          <button type="submit" className="btn btn-accent" disabled={busy}>{t('vault.add')}</button>
        </div>
      </form>

      <div className="vault-toolbar">
        <input className="prop-choice__text vault-search" value={q}
          onChange={(e) => setQ(e.target.value)} placeholder={t('vault.searchPlaceholder')} />
        {all.length > 0 && (
          <div className="vault-export">
            <span className="vault-export__label">{t('vault.export')}</span>
            <button type="button" className="vault-kind-tab"
              onClick={() => downloadText('knowledge-vault.md', notesToMarkdown(all), 'text/markdown')}>
              {t('vault.exportMd')}
            </button>
            <button type="button" className="vault-kind-tab"
              onClick={() => downloadText('knowledge-vault.json', notesToJson(all), 'application/json')}>
              {t('vault.exportJson')}
            </button>
          </div>
        )}
        <div className="vault-filters">
          <button type="button" className={`vault-kind-tab${filter === 'all' ? ' is-active' : ''}`}
            onClick={() => setFilter('all')}>{t('vault.allKinds')}</button>
          {KINDS.map((k) => (
            <button key={k} type="button"
              className={`vault-kind-tab${filter === k ? ' is-active' : ''}`}
              onClick={() => setFilter(k)}>{t(KIND_KEY[k])}</button>
          ))}
        </div>
      </div>

      {state.status === 'success' && notes.length === 0 && (
        <EmptyState title={t('vault.title')} description={t('vault.empty')} />
      )}

      <ul className="vault-list">
        {notes.map((n) => (
          <li key={n.id} className="vault-note">
            <div className="vault-note__head">
              <span className={`vault-note__kind vault-note__kind--${n.kind}`}>{t(KIND_KEY[n.kind])}</span>
              <button type="button" className="vault-note__del" onClick={() => remove(n)}>{t('vault.delete')}</button>
            </div>
            <p className="vault-note__content">{n.content}</p>
            {n.tags.length > 0 && (
              <div className="vault-note__tags">
                {n.tags.map((tg) => <span key={tg} className="vault-note__tag">#{tg}</span>)}
              </div>
            )}
          </li>
        ))}
      </ul>
    </main>
  );
}
